/*
 * <filter-search> — type-to-narrow for a long filter list (Brand, Ingredient, ...).
 *
 * Purely client side: it hides non-matching <li> in the sibling list and never touches
 * the filter URLs, so Search & Discovery behaviour and the theme's filter-item handlers
 * are unaffected.
 *
 * The collection sidebar is rendered three times (drawer / vertical / horizontal), so
 * this file can be requested more than once and must not re-define the element. It also
 * avoids element ids for the same reason and finds its list by DOM position.
 */
(function () {
  if (window.customElements && customElements.get('filter-search')) return;

  // Case- and diacritic-insensitive, so "loreal" matches "L'Oréal" and Arabic
  // brand names match regardless of harakat.
  const fold = (value) =>
    (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯؐ-ًؚ-ٰٟۖ-ۭ]/g, '')
      .replace(/[ـ]/g, '')
      .replace(/[’'`´]/g, '')
      .toLowerCase()
      .trim();

  class FilterSearch extends HTMLElement {
    connectedCallback() {
      if (this.__wired) return;
      this.__wired = true;

      this.input = this.querySelector('.filter-search__input');
      this.clearButton = this.querySelector('.filter-search__clear');
      this.status = this.querySelector('.filter-search__status');
      this.list = this.parentElement && this.parentElement.querySelector('ul.items');

      if (!this.input || !this.list) return;

      // Match on the option name only. The match count lives in the same element,
      // so without stripping it a query of "0" would match every option showing (0).
      const nameOf = (li) => {
        const counts = li.querySelectorAll('.filter-value-count');
        if (!counts.length) return li.textContent;
        const clone = li.cloneNode(true);
        clone.querySelectorAll('.filter-value-count').forEach((n) => n.remove());
        return clone.textContent;
      };

      this.items = Array.from(this.list.children).map((li) => ({
        el: li,
        text: fold(nameOf(li)),
      }));

      this.emptyText = this.dataset.emptyText || 'No matches';
      this.countText = this.dataset.countText || '';

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          this.input.value = '';
          this.apply();
          this.input.focus();
        });
      }
    }

    onKeydown(event) {
      // Escape clears the box rather than closing the drawer around it.
      if (event.key === 'Escape' && this.input.value !== '') {
        event.stopPropagation();
        event.preventDefault();
        this.input.value = '';
        this.apply();
      }
    }

    onInput() {
      this.apply();
    }

    apply() {
      const query = fold(this.input.value);
      let shown = 0;

      this.items.forEach((item) => {
        const match = query === '' || item.text.indexOf(query) !== -1;
        item.el.hidden = !match;
        if (match) shown += 1;
      });

      if (this.clearButton) this.clearButton.hidden = query === '';
      this.toggleEmptyState(query !== '' && shown === 0);

      if (this.status) {
        this.status.textContent =
          query === ''
            ? ''
            : shown === 0
              ? this.emptyText
              : this.countText.replace('[count]', shown);
      }
    }

    toggleEmptyState(isEmpty) {
      if (isEmpty && !this.emptyEl) {
        this.emptyEl = document.createElement('p');
        this.emptyEl.className = 'filter-search__empty';
        this.emptyEl.textContent = this.emptyText;
        this.list.insertAdjacentElement('afterend', this.emptyEl);
      } else if (!isEmpty && this.emptyEl) {
        this.emptyEl.remove();
        this.emptyEl = null;
      }
    }
  }

  customElements.define('filter-search', FilterSearch);
})();
